// Native request gateway. Installed as the axios adapter on native only. Routes GETs
// through the local encrypted cache (offline-capable reads) and routes financial writes
// local-first + outbox (offline-capable writes). Everything else passes through to network.
import { newId } from "./db";
import {
  setCache, getCache, patchCaches, upsertRecord, upsertMany, getRecord, removeRecord, enqueue,
  findPendingCreate, updateOutboxBody, listRecords, readPractices, readLocalList,
  markAgreementsRefreshed, agreementRefreshedAt, replacePracticeAgreements, clearAllLocalData,
} from "./repo";
import { processOp, isNetworkError } from "./sync";
import { computeExpectedCents, pickAgreementForDate } from "./calc";

// Record-collection GETs are served REPOSITORY-FIRST from the encrypted SQLite tables so
// they work offline with NO cache warming. Computed/derived responses use api_cache.
const READ_EXCLUDE = ["/accountant/pack", "/accountant/send", "/uploads", "/files"];
const COMPUTED_PREFIXES = ["/dashboard", "/bas", "/trends", "/meta", "/reserves/summary",
  "/expenses/categories", "/auth/me", "/billing/status"];
const COLLECTION_MATCH = [
  ["/practices", "practices"], ["/payment-reports", "payment-reports"], ["/work", "work"],
  ["/payments", "payments"], ["/expenses", "expenses"], ["/adjustments", "adjustments"],
  ["/reserves", "reserves"], ["/settings", "settings"], ["/accountant/profile", "accountant"],
];
const TABLE_BY_COLLECTION = {
  practices: "practices", work: "work_entries", payments: "payments",
  "payment-reports": "payment_reports", expenses: "expenses", adjustments: "adjustments",
  reserves: "reserve_deposits", settings: "settings", accountant: "accountant_profiles",
};
// list-read config: month field to filter on, attach practice_name, soft-delete filtering
const COLLS = {
  work: { monthField: "date", attachPractice: true },
  payments: { monthField: "payment_date", attachPractice: true },
  "payment-reports": { monthField: "report_date", softDelete: true, attachPractice: true },
  expenses: { monthField: "date", attachPractice: true },
  adjustments: { attachPractice: true },
  reserves: {},
};

// Financial write entities that are offline-capable (local-first + outbox mirror).
const WRITE_ENTITIES = {
  "/work": { entity: "work", table: "work_entries", pathPrefix: "/work" },
  "/payments": { entity: "payments", table: "payments", pathPrefix: "/payments" },
  "/expenses": { entity: "expenses", table: "expenses", pathPrefix: "/expenses" },
  "/adjustments": { entity: "adjustments", table: "adjustments", pathPrefix: "/adjustments" },
  "/reserves": { entity: "reserves", table: "reserve_deposits", pathPrefix: "/reserves" },
  "/payment-reports": { entity: "payment-reports", table: "payment_reports", pathPrefix: "/payment-reports" },
  "/settings": { entity: "settings", table: "settings", pathPrefix: "/settings", single: true },
  "/accountant/profile": { entity: "accountant-profile", table: "accountant_profiles", pathPrefix: "/accountant/profile", single: true },
};

const pathOf = (url) => (url || "").split("?")[0];
const segs = (p) => p.split("/").filter(Boolean);

// Build 12 — session-fresh agreement trust. `_sessionStart` marks the current app launch.
// Offline financial calc trusts a practice's local agreement ONLY if it was authoritatively
// reconciled from the server at/after this launch. A cold launch reinitialises the JS bundle
// (new _sessionStart); beginSession() is also called at boot for explicitness/tests.
let _sessionStart = new Date().toISOString();
export function beginSession() { _sessionStart = new Date().toISOString(); }
export function getSessionStart() { return _sessionStart; }

// Authoritatively reconcile the local store from a server /practices LIST response: refresh
// each practice record (incl. its embedded current agreement), upsert that current agreement,
// and stamp the practice trusted for THIS session. Used by app-open / reconnect reconciliation
// (Option B) so subsequent OFFLINE calculations use the current — not stale — agreement.
export async function reconcilePracticesFromServer(list) {
  if (!_exec || !Array.isArray(list)) return;
  await upsertMany(_exec, "practices", list);
  for (const p of list) {
    if (p && p.id && p.agreement && p.agreement.id) {
      await upsertRecord(_exec, "agreement_versions", p.agreement);
      await markAgreementsRefreshed(_exec, _userId, p.id);
    }
  }
}

function resolveRead(path) {
  if (READ_EXCLUDE.some((p) => path.startsWith(p))) return { kind: "passthrough" };
  if (COMPUTED_PREFIXES.some((p) => path === p || path.startsWith(p + "?") || path.startsWith(p + "/")))
    return { kind: "read", mode: "cache" };
  if (/^\/practices\/[^/]+\/payment-check/.test(path)) return { kind: "read", mode: "cache" };
  const found = COLLECTION_MATCH.find(([pre]) =>
    path === pre || path.startsWith(pre + "?") || path.startsWith(pre + "/"));
  if (!found) return { kind: "passthrough" };
  const [pre, collection] = found;
  const rest = path.slice(pre.length).replace(/^\//, "");
  if (rest.includes("/")) return { kind: "passthrough" }; // deeper subpaths (e.g. /{id}/agreement)
  return { kind: "read", mode: "repo", collection, detailId: rest || null,
    listPath: pre, table: TABLE_BY_COLLECTION[collection] };
}

function resolveWrite(method, path) {
  const single = segs(path).length;
  for (const [base, cfg] of Object.entries(WRITE_ENTITIES)) {
    const baseSegs = segs(base).length;
    if (cfg.single) {
      if (path === base && (method === "PUT" || method === "POST")) return { kind: "write", ...cfg, isCreate: false };
      continue;
    }
    if (method === "POST" && path === base) return { kind: "write", ...cfg, isCreate: true };
    if ((method === "PUT" || method === "DELETE") && path.startsWith(base + "/") && single === baseSegs + 1) {
      return { kind: "write", ...cfg, isCreate: false, entityId: segs(path)[segs(path).length - 1] };
    }
  }
  return { kind: "passthrough" };
}

export function resolveRoute(method, url) {
  const path = pathOf(url);
  return method === "GET" ? resolveRead(path) : resolveWrite(method, path);
}

function synth(config, data, status = 200) {
  return { data, status, statusText: "OK", headers: {}, config, request: { fromLocal: true } };
}
const online = () => (_netOnline !== null
  ? _netOnline
  : !(typeof navigator !== "undefined" && navigator.onLine === false));
let _netOnline = null;
// Reliable connectivity from @capacitor/network (WKWebView navigator.onLine is unreliable).
export function setNetOnline(v) { _netOnline = v; }

// The gateway needs a raw (non-gateway) network request fn + the current user id.
let _net = null;
let _exec = null;
let _userId = null;
export function configureGateway({ net, exec, userId }) {
  if (net) _net = net;
  if (exec) _exec = exec;
  if (userId !== undefined) _userId = userId;
}

function netConfig(config) {
  const c = { ...config };
  delete c.adapter; // prevent recursion back into the gateway
  return c;
}

// Reproduce the work entry's expected_cents locally (offline) from the seeded agreement so
// the Work card shows the real amount, not $0.00. If it cannot be reproduced confidently,
// mark _calc_pending so the UI shows a truthful "Pending" state. Server stays authoritative
// and overwrites this record (with its own snapshot) on reconnect/reconcile.
async function enrichWorkLocal(rec) {
  try {
    // Session-fresh trust (Build 12): compute ONLY from a locally-trusted agreement whose
    // reconciliation happened during THIS app session. A persistent trust marker from a prior
    // session no longer blesses now-stale local data (that produced the $600-instead-of-$400
    // upgraded-device defect). If not freshly reconciled this session, stay Pending; the server
    // reconciles the authoritative amount on reconnect.
    let agr = null;
    const refreshedAt = await agreementRefreshedAt(_exec, rec.practice_id);
    if (refreshedAt != null && String(refreshedAt) >= _sessionStart) {
      // Use the practice's CURRENT agreement (server-authoritative, kept fresh by reconciliation)
      // — the same rule the server's create_work applies to a newly created (today-dated) entry.
      // Preferring it avoids selecting a leftover STALE agreement_versions row on upgraded installs
      // (e.g. an old percentage/6000 row with effective_end=null) that would mis-calculate.
      const practices = await readPractices(_exec, _userId);
      const prac = practices.find((p) => String(p.id) === String(rec.practice_id));
      if (prac && prac.agreement && prac.agreement.calc_method) agr = prac.agreement;
      if (!agr) {
        const ags = await listRecords(_exec, "agreement_versions", _userId);
        agr = pickAgreementForDate(ags, rec.practice_id, rec.date);
      }
    }
    const expected = agr ? computeExpectedCents(agr, rec) : null;
    if (expected != null && Number.isFinite(expected)) {
      rec.expected_cents = expected;
      rec.snapshot = { ...(rec.snapshot || {}), calc_method: agr.calc_method, expected_cents: expected, _local: true };
      delete rec._calc_pending;
    } else {
      rec._calc_pending = true; // untrusted/unresolved agreement -> Pending, never a wrong number
    }
  } catch {
    rec._calc_pending = true;
  }
  if (rec.received_cents == null) rec.received_cents = 0;
  if (!rec.status) rec.status = "expected";
}

// Build a PracticeDetail-consumable detail shape offline from local repository data, so the
// screen renders instead of crashing on a raw practice record. Server-derived labels are left
// null (the page shows an "unavailable offline" state for those) and _partial flags the view.
async function buildLocalPracticeDetail(id) {
  const uid = _userId;
  const list = await readPractices(_exec, uid);
  const p = list.find((x) => String(x.id) === String(id)) || (await getRecord(_exec, "practices", id));
  if (!p) return null;
  const agr = p.agreement || null;
  const entries = (await readLocalList(_exec, "work_entries", uid, { practiceId: id }))
    .sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")));
  const payments = (await readLocalList(_exec, "payments", uid, { practiceId: id }))
    .sort((a, b) => String(b.payment_date || "").localeCompare(String(a.payment_date || "")));
  const expected = entries.reduce((s, e) => s + (e._calc_pending ? 0 : (e.expected_cents || 0)), 0);
  const received = entries.reduce((s, e) => s + (e.received_cents || 0), 0);
  return {
    practice: p, current_agreement: agr, agreements: agr ? [agr] : [],
    engagement_label: null, method_label: null, rate_label: null, lab_label: null,
    freq_label: null, super_label: null, gst_label: null,
    working_days_labels: p.working_days || [],
    expected_cents: expected, received_cents: received,
    outstanding_cents: Math.max(expected - received, 0),
    entries, payments, _partial: true,
  };
}

function parseQuery(url) {
  const q = new URLSearchParams(url.split("?")[1] || "");
  return { month: q.get("month") || undefined, practice_id: q.get("practice_id") || undefined,
    deleted: q.get("deleted") || undefined, type: q.get("type") || undefined };
}

// Read structured records straight from the seeded encrypted SQLite tables (repository-first).
// Query-string variants (e.g. /practices vs /practices?month=X) resolve to the SAME table.
async function readLocalCollection(route) {
  const uid = _userId;
  const { collection, detailId } = route;
  if (collection === "practices") {
    const list = await readPractices(_exec, uid); // joins current agreement locally
    if (detailId) return list.find((p) => String(p.id) === String(detailId))
      || (await getRecord(_exec, "practices", detailId));
    return list;
  }
  if (collection === "settings") return (await getRecord(_exec, "settings", uid)) || {};
  if (collection === "accountant") return (await getRecord(_exec, "accountant_profiles", uid)) || {};
  const table = route.table;
  if (detailId) return await getRecord(_exec, table, detailId);
  const cfg = COLLS[collection] || {};
  const params = route._params || {};
  const list = await readLocalList(_exec, table, uid, {
    month: params.month, monthField: cfg.monthField, practiceId: params.practice_id,
    deleted: params.deleted === "true" ? true : (cfg.softDelete ? false : undefined),
  });
  if (cfg.attachPractice) {
    const pracs = await listRecords(_exec, "practices", uid);
    const nameById = Object.fromEntries(pracs.map((p) => [String(p.id), p.name]));
    return list.map((r) => (r.practice_name ? r : { ...r, practice_name: nameById[String(r.practice_id)] }));
  }
  return list;
}

async function localRead(route, config) {
  if (route.mode === "repo") {
    // Practice DETAIL is a server-COMPUTED shape. Offline: serve the last cached computed
    // response if present, else build a consumable shape locally. Never return a raw practice
    // record (which crashes PracticeDetail's data.practice / p.name access).
    if (route.collection === "practices" && route.detailId) {
      const cached = await getCache(_exec, config.url);
      if (cached && cached.practice) return synth(config, cached);
      return synth(config, await buildLocalPracticeDetail(route.detailId));
    }
    route._params = parseQuery(config.url);
    const data = await readLocalCollection(route);
    return synth(config, data === undefined ? (route.detailId ? null : []) : data);
  }
  const c = await getCache(_exec, config.url); // computed/derived responses
  return synth(config, c !== undefined ? c : null);
}

async function handleRead(route, config) {
  const url = config.url;
  if (online()) {
    try {
      const res = await _net.request(netConfig(config));
      await setCache(_exec, url, res.data);
      if (route.table) { // keep the local structured store fresh from server responses
        if (Array.isArray(res.data)) await upsertMany(_exec, route.table, res.data, { srvDeleted: url.includes("deleted=true") });
        else if (res.data && (res.data.id || res.data.user_id)) await upsertRecord(_exec, route.table, res.data);
      }
      // Authoritative agreement reconciliation (data-safe, per-practice): keep the offline calc
      // trustworthy. LIST supplies only the current agreement -> upsert it + mark the practice
      // trusted. DETAIL supplies the full authoritative history -> REPLACE the practice's local
      // set (removing stale/superseded rows) + mark trusted. Never touches financial records.
      if (route.collection === "practices") {
        if (Array.isArray(res.data)) {
          for (const p of res.data) if (p && p.id && p.agreement && p.agreement.id) {
            await upsertRecord(_exec, "agreement_versions", p.agreement);
            await markAgreementsRefreshed(_exec, _userId, p.id);
          }
        } else if (res.data && res.data.practice && res.data.practice.id) {
          const vers = res.data.agreements
            || (res.data.current_agreement ? [res.data.current_agreement] : []);
          if (vers.length) await replacePracticeAgreements(_exec, _userId, res.data.practice.id, vers);
          else await markAgreementsRefreshed(_exec, _userId, res.data.practice.id);
        }
      }
      return res;
    } catch (e) {
      if (isNetworkError(e)) return localRead(route, config); // repository-first fallback
      throw e;
    }
  }
  return localRead(route, config);
}

async function handleWrite(route, config) {
  const method = (config.method || "get").toUpperCase();
  const body = typeof config.data === "string" ? JSON.parse(config.data || "{}") : (config.data || {});
  const { table, entity, pathPrefix, single, isCreate } = route;
  const entityId = single ? _userId : (isCreate ? newId() : route.entityId);

  // 1) Commit locally first (durable, encrypted) so the write survives offline/kill/restart.
  let temp = null;
  if (method === "DELETE") {
    await removeRecord(_exec, table, entityId);
    await patchCaches(_exec, pathPrefix, { op: "remove", id: entityId });
  } else {
    const prev = single || !isCreate ? (await getRecord(_exec, table, entityId)) || {} : {};
    temp = { ...prev, ...body, id: entityId };
    if (!temp.user_id) temp.user_id = _userId;
    if (isCreate && !temp.created_at) temp.created_at = new Date().toISOString();
    temp._pending = true;
    if (entity === "work") await enrichWorkLocal(temp); // local expected_cents (or Pending)
    await upsertRecord(_exec, table, temp, { pending: 1 });
    if (single) await setCache(_exec, pathPrefix, temp);
    else await patchCaches(_exec, pathPrefix, { op: "upsert", item: temp });
  }

  // 2) Enqueue the server-mirror op (persisted; survives process termination).
  const key = newId();
  const meta = { entity, table, pathPrefix, single, isCreate, entityId };
  // Fold an edit of a not-yet-synced create into that create's body (dependency safety).
  const pendingCreate = !isCreate && method !== "DELETE"
    ? await findPendingCreate(_exec, entity, entityId) : null;
  if (pendingCreate) {
    await updateOutboxBody(_exec, pendingCreate.key, { ...pendingCreate.body, ...body });
  } else {
    await enqueue(_exec, { key, method, url: config.url, body, meta });
  }

  // 3) Mirror now if online; reconcile computed/server fields back into local.
  if (online() && !pendingCreate) {
    try {
      const res = await processOp(_exec, _net, { key, method, url: config.url, body, meta });
      return res;
    } catch (e) {
      if (!isNetworkError(e)) throw e; // surface real validation errors to the UI
      // network dropped mid-flight: keep local + queued, fall through to pending response
    }
  }
  return synth(config, method === "DELETE" ? { ok: true, _pending: true } : temp);
}

// Reset Data is a destructive server + local operation. Require connectivity so the server
// authoritatively wipes first; THEN wipe every local store so the data cannot reappear from the
// device on refresh/restart/reconnect/reconciliation. Preserve identity/billing cache so the
// authenticated session and subscription entitlement survive (account/auth/Stripe untouched).
async function handleAccountReset(config) {
  if (!online()) {
    const err = new Error("Network Error");
    err.code = "ERR_NETWORK";
    throw err;
  }
  const res = await _net.request(netConfig(config)); // server clears data, keeps account/subscription
  // Server wipe already succeeded. If the local cleanup now fails, surface an ACCURATE
  // local-cleanup error (not the raw SQLite message) so the UI never implies the server data
  // still exists. Local state is recovered on next app open (empty-server reconcile/self-heal).
  try {
    await clearAllLocalData(_exec, { preserveCacheUrls: ["/auth/me", "/billing/status"] });
  } catch (e) {
    const err = new Error(
      "Your account data was reset on the server, but clearing this device did not fully complete. Reopen the app to finish clearing local data.",
    );
    err.code = "ERR_LOCAL_RESET_CLEANUP";
    err.serverResetOk = true;
    err.cause = e;
    throw err;
  }
  return res;
}

export function makeGatewayAdapter() {
  return async function gatewayAdapter(config) {
    if (!_exec || !_net) { // not booted yet — behave as plain network
      return _net ? _net.request(netConfig(config)) : Promise.reject(new Error("gateway not configured"));
    }
    const method = (config.method || "get").toUpperCase();
    if (method === "POST" && pathOf(config.url) === "/account/reset") return handleAccountReset(config);
    const route = resolveRoute(method, config.url);
    if (route.kind === "read") return handleRead(route, config);
    if (route.kind === "write") return handleWrite(route, config);
    return _net.request(netConfig(config)); // passthrough
  };
}
